import React, { useState } from "react";
import { WhatsAppMessage } from "../../types/api";

interface MessageBubbleProps {
  message: WhatsAppMessage;
  isOwn: boolean;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jerusalem" });
}

function StatusTick({ status }: { status: WhatsAppMessage["status"] }) {
  const doubleStyle = (color: string): React.CSSProperties => ({
    color,
    fontSize: 14,
    letterSpacing: "-3px",
    display: "inline-block",
    width: 18,
  });
  if (status === "queued" || status === "sent") return <span style={{ color: "#aaa", fontSize: 14 }}>✓</span>;
  if (status === "delivered") return <span style={doubleStyle("#aaa")}>✓✓</span>;
  if (status === "read") return <span style={doubleStyle("#53bdeb")}>✓✓</span>;
  if (status === "failed") return <span style={{ color: "#ef4444", fontSize: 14 }}>!</span>;
  return null;
}

function fileName(url: string) {
  try {
    const parts = new URL(url).pathname.split("/");
    return decodeURIComponent(parts[parts.length - 1]) || "קובץ";
  } catch {
    return url.split("/").pop() || "קובץ";
  }
}

function DownloadButton({ url, name }: { url: string; name?: string }) {
  return (
    <a
      href={url}
      download={name || fileName(url)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginTop: 5,
        fontSize: 12,
        color: "#555",
        textDecoration: "none",
        padding: "3px 8px",
        borderRadius: 6,
        backgroundColor: "rgba(0,0,0,0.07)",
      }}
    >
      ⬇ הורד
    </a>
  );
}

function MediaContent({ url, name }: { url: string; name?: string }) {
  const [imageError, setImageError] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const displayName = name || fileName(url);

  // Always try image first; fall back to file view on load error
  if (!imageError) {
    return (
      <>
        <div style={{ marginBottom: 2 }}>
          <img
            src={url}
            alt="תמונה"
            onError={() => setImageError(true)}
            onClick={() => setLightbox(true)}
            style={{
              maxWidth: "100%",
              maxHeight: 220,
              borderRadius: 8,
              cursor: "zoom-in",
              display: "block",
            }}
          />
          <DownloadButton url={url} name={name} />
        </div>

        {lightbox && (
          <div
            onClick={() => setLightbox(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.87)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              cursor: "zoom-out",
              gap: 14,
            }}
          >
            <img
              src={url}
              alt="תמונה"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 8, display: "block", cursor: "default" }}
            />
            <DownloadButton url={url} name={name} />
          </div>
        )}
      </>
    );
  }

  // Image failed to load — render as downloadable file
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={displayName}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        backgroundColor: "rgba(0,0,0,0.06)",
        borderRadius: 8,
        marginBottom: 4,
        textDecoration: "none",
        color: "#111",
        fontSize: 13,
      }}
    >
      <span style={{ fontSize: 22 }}>📄</span>
      <span style={{ flex: 1, wordBreak: "break-all" }}>{displayName}</span>
      <span style={{ fontSize: 16, color: "#555", flexShrink: 0 }}>⬇</span>
    </a>
  );
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn }) => {
  const isOutbound = message.direction === "outbound";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isOutbound ? "flex-end" : "flex-start",
        marginBottom: 4,
        paddingLeft: isOutbound ? 60 : 8,
        paddingRight: isOutbound ? 8 : 60,
      }}
    >
      <div
        style={{
          maxWidth: "70%",
          backgroundColor: isOutbound ? "#DCF8C6" : "#ffffff",
          borderRadius: isOutbound ? "12px 2px 12px 12px" : "2px 12px 12px 12px",
          padding: "8px 12px 6px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        {/* Sender name */}
        {message.sender_user_name && (
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3A7380", marginBottom: 2 }}>
            {message.sender_user_name}
          </div>
        )}

        {/* Media */}
        {message.media_url && <MediaContent url={message.media_url} name={message.file_name} />}

        {/* Message body */}
        {message.body && (() => {
          if (message.is_template) {
            const newlineIdx = message.body.indexOf("\n");
            const headerLine = newlineIdx !== -1 ? message.body.slice(0, newlineIdx) : message.body;
            const bodyRest  = newlineIdx !== -1 ? message.body.slice(newlineIdx + 1) : "";
            return (
              <div style={{ fontSize: 14, color: "#111", lineHeight: 1.5, direction: "rtl" }}>
                <div style={{ fontWeight: 700, marginBottom: bodyRest ? 4 : 0 }}>{headerLine}</div>
                {bodyRest && <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{bodyRest}</div>}
              </div>
            );
          }
          return (
            <div
              style={{
                fontSize: 14,
                color: "#111",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                direction: "rtl",
              }}
            >
              {message.body}
            </div>
          );
        })()}

        {/* Time + status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 3,
            marginTop: 2,
          }}
        >
          <span style={{ fontSize: 11, color: "#999" }}>{formatTime(message.created_at)}</span>
          {isOutbound && <StatusTick status={message.status} />}
        </div>
      </div>
    </div>
  );
};
